import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { memo } from "react";
import type { ChatCompletionsMessageObject } from "../../../services/api/instances/completionsApi/types";
import { AppLoading } from "../../../shared/components/AppLoading";
import styles from "./styles.module.css";
import { SystemMessage } from "./SystemMessage";
import { UserMessage } from "./UserMessage";
import { generateId } from "../../../shared/utils/generateId";

interface Props {
  messages: ChatCompletionsMessageObject[];
  isLoading?: boolean;
  loadingMessage?: string | React.ReactNode;
  isResizable?: boolean;
  onFileClick?: (url: string, filename: string) => void;
  className?: string;
}

const DisplayedMessagesInner = ({
  messages,
  isLoading,
  loadingMessage,
  isResizable,
  onFileClick,
  className,
}: Props) => {
  const { t } = useTranslation();
  return (
    <div
      className={classNames(
        styles.wrapper,
        isResizable && styles["resize-container"],
        className,
      )}
    >
      {messages?.length ? (
        <>
          {messages.map((message, index) => {
            if (message.role === "user") {
              return (
                <UserMessage
                  key={message.id ? message.id : `${message.role}-${index}`}
                  userMessage={message.content as string}
                />
              );
            }
            if (message.role === "assistant") {
              return (
                <SystemMessage
                  key={message.id ? message.id : `${message.role}-${index}`}
                  systemMessage={message.content as string}
                  onFileClick={onFileClick}
                />
              );
            }
          })}
        </>
      ) : (
        <div className={styles.empty}>{t("chat_modal.chat_placeholder")}</div>
      )}
      {isLoading && (
        <div className={styles.loading}>
          {typeof loadingMessage === "string" && loadingMessage !== "" ? (
            <div className={styles["loading-message"]}>{loadingMessage}</div>
          ) : (
            <div className={styles["loading-message"]}>
              <>{loadingMessage}</>
            </div>
          )}
          <AppLoading small className={styles["chat-loader"]} />
        </div>
      )}
    </div>
  );
};

export const DisplayedMessages = memo(DisplayedMessagesInner);
